import { z } from 'zod';

/**
 * The message body schema (D-045, PLAN §3.1 "Message body storage"): a TipTap/ProseMirror
 * document restricted to this whitelist of nodes and marks. The web composer, the mobile renderer
 * and the server validator (`validate.ts`) all use it; anything outside it is rejected with
 * `422 invalid_rich_text`.
 *
 * Nodes: `doc`, `paragraph`, `text`, `hardBreak`, `blockquote`, `codeBlock` (`language`),
 * `bulletList`, `orderedList` (`start`), `listItem`, `mention` (`kind`: user | group | channel |
 * here, `id`), `channelLink` (`id`), `emoji` (`name`). Marks: `bold`, `italic`, `strike`, `code`,
 * `link` (`href`: http, https or mailto).
 *
 * TipTap composers must turn off what StarterKit adds beyond this (heading, horizontalRule,
 * underline) and name their custom nodes `mention`, `channelLink` and `emoji` with these attrs.
 * TipTap's extra presentation attrs (`link.target`/`rel`/`class`, `mention.mentionSuggestionChar`,
 * `orderedList.type`) are accepted and dropped; any other unknown key is an error.
 */

export const RICHTEXT_BLOCK_TYPES = [
  'paragraph',
  'blockquote',
  'codeBlock',
  'bulletList',
  'orderedList',
] as const;
export const RICHTEXT_INLINE_TYPES = [
  'text',
  'hardBreak',
  'mention',
  'channelLink',
  'emoji',
] as const;
export const RICHTEXT_NODE_TYPES = [
  'doc',
  ...RICHTEXT_BLOCK_TYPES,
  'listItem',
  ...RICHTEXT_INLINE_TYPES,
] as const;
export type RichTextNodeType = (typeof RICHTEXT_NODE_TYPES)[number];

export const RICHTEXT_MARK_TYPES = ['bold', 'italic', 'strike', 'code', 'link'] as const;
export type RichTextMarkType = (typeof RICHTEXT_MARK_TYPES)[number];

/** `mention.attrs.kind`. `channel` and `here` use their own kind as the id. */
export const MENTION_NODE_KINDS = ['user', 'group', 'channel', 'here'] as const;
export type MentionNodeKind = (typeof MENTION_NODE_KINDS)[number];

/** Link targets: http, https and mailto only (no `javascript:`, `data:`, relative URLs). */
export const LINK_HREF_PATTERN = /^(?:https?:\/\/[^\s<>"'`]+|mailto:[^\s<>"'`]+)$/i;
export const MAX_LINK_HREF_LENGTH = 2048;

/** `codeBlock.attrs.language`: a highlight.js / lowlight language name such as `ts` or `c++`. */
export const CODE_LANGUAGE_PATTERN = /^[a-z0-9][a-z0-9+#._-]{0,31}$/;

export type RichTextMark =
  | { type: 'bold' }
  | { type: 'italic' }
  | { type: 'strike' }
  | { type: 'code' }
  | { type: 'link'; attrs: { href: string } };

export interface TextNode {
  type: 'text';
  text: string;
  marks?: RichTextMark[];
}

export interface HardBreakNode {
  type: 'hardBreak';
}

export interface MentionNode {
  type: 'mention';
  attrs: {
    kind: MentionNodeKind;
    /** A user or group id, or `channel` / `here`. */
    id: string;
    /** Display snapshot from the composer. Renderers resolve the id instead; never trusted. */
    label?: string | null;
  };
}

export interface ChannelLinkNode {
  type: 'channelLink';
  attrs: { id: string; label?: string | null };
}

export interface EmojiNode {
  type: 'emoji';
  attrs: { name: string };
}

export type InlineNode = TextNode | HardBreakNode | MentionNode | ChannelLinkNode | EmojiNode;

export interface ParagraphNode {
  type: 'paragraph';
  content?: InlineNode[];
}

export interface CodeBlockNode {
  type: 'codeBlock';
  attrs?: { language: string | null };
  /** Plain text only (no marks). */
  content?: TextNode[];
}

export interface BlockquoteNode {
  type: 'blockquote';
  content: BlockNode[];
}

export interface ListItemNode {
  type: 'listItem';
  /** A paragraph first, then any blocks (TipTap `paragraph block*`). */
  content: BlockNode[];
}

export interface BulletListNode {
  type: 'bulletList';
  content: ListItemNode[];
}

export interface OrderedListNode {
  type: 'orderedList';
  attrs?: { start: number };
  content: ListItemNode[];
}

export type BlockNode =
  ParagraphNode | CodeBlockNode | BlockquoteNode | BulletListNode | OrderedListNode;

/** A message body (`messages.content_json`, `MessageDto.content`). */
export interface RichTextDoc {
  type: 'doc';
  content: BlockNode[];
}

/**
 * Wire schema for `content` in requests and responses: an object that looks like a document.
 * It is deliberately shallow (OpenAPI stays small and a malformed body is a 422 with a precise
 * path, not a 400): the server runs `validateRichText()` on every write, which is the real check.
 */
export const richTextDocWireSchema = z
  .looseObject({
    type: z.literal('doc'),
    content: z.array(z.record(z.string(), z.unknown())),
  })
  .meta({ description: 'ProseMirror (TipTap) document; see @orbit/shared/richtext (D-045).' });

/** A document with one empty paragraph: what an empty composer produces. */
export function emptyDoc(): RichTextDoc {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/** One paragraph of plain text (system messages, tests, simple clients). */
export function textDoc(text: string): RichTextDoc {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
  };
}
